import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import PriceDisplay from '../../../../../../../../../components/global/PriceDisplay';
import Button from '../../../../../../../../../components/global/Button';
import { CURRENCY } from '../../../../../../../../../constants/currency';
import { getPhotoDownloadCartItem } from '../../../../../../../../../services/checkout/redux/helpers';
import { getAsset } from '../../../../../../../../../util/assets';
import Badge from '../../../../../../../../../components/global/Badge';
import AddedToCartLabel from '../../AddedToCartLabel';
import useOpenModal from '../../../../../../../../../hooks/useOpenModal';
import { REQUIRES_PRINT_PACKAGE_ALERT_MODAL } from '../../../PurchasingOptions/components/RequiresPrintPackageAlert';
import useOpenAlert from '../../../../../../../../../hooks/useOpenAlert';

const propTypes = {
  webGalleryPrice: PropTypes.shape({
    value: PropTypes.number.isRequired,
    currency: PropTypes.oneOf(Object.values(CURRENCY)),
  }).isRequired,
  cart: PropTypes.array.isRequired,
  photo: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  album: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  onPhotoDownloadToggle: PropTypes.func.isRequired,
  className: PropTypes.string,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool.isRequired,
  included: PropTypes.bool,
};

const PhotoDownloadOption = ({
  webGalleryPrice,
  cart,
  photo,
  album,
  onPhotoDownloadToggle,
  className,
  readOnly,
  disabled,
  included,
}) => {
  const openModal = useOpenModal();
  const openAlert = useOpenAlert();
  const classes = classNames({
    [className]: !!className,
    'purchasing-option-item': true,
    'purchasing-option-item-digital-download': true,
  });

  const inCart = getPhotoDownloadCartItem(cart, {
    photoId: photo.id,
    albumId: album.id,
  });

  const onToggle = () => {
    if (disabled) {
      openModal(REQUIRES_PRINT_PACKAGE_ALERT_MODAL, {
        name: 'Hi-Res Download',
      });
      return;
    }

    if (included) {
      openAlert({
        type: 'info',
        asset: '/icon-package-3d.svg',
        message: (
          <h4 className="text-center mb-1">
            Hi-res digital download is already included in one of the print
            packages in your cart.
          </h4>
        ),
        btnLabel: 'OK',
      });
      return;
    }

    onPhotoDownloadToggle({
      photo,
      album,
      webGalleryPrice,
    });
  };

  return (
    <div className={classes}>
      <div className="purchasing-option-item-thumb">
        <img
          width={60}
          src={getAsset('/digital-download-illustration.svg')}
          alt=""
        />
      </div>

      <div className="purchasing-option-item-content-wrap">
        <div className="purchasing-option-item-title">
          <div className="text-truncate">Hi&ndash;Res Download</div>
          <div className="purchasing-option-item-info-extra">
            <Badge badgeStyle="yellow" className="text-extra-bold">
              <PriceDisplay
                price={webGalleryPrice.value}
                currency={webGalleryPrice.currency}
              />
            </Badge>
          </div>
        </div>

        <div className="purchasing-option-item-content mb-2">
          {!readOnly ? (
            <p className="text-sm lh-normal text-dark text-bold">
              Unlock access to hi&ndash;res digital downloads for this photo!
            </p>
          ) : (
            <p className="text-sm lh-normal text-dark text-bold">
              Unlock access to hi&ndash;res digital downloads (price per photo)!
            </p>
          )}

          {disabled && !readOnly && (
            <span className="purchasing-option-item-info-requires-package">
              Requires the purchase of a print package first.
            </span>
          )}
        </div>

        {!readOnly && (
          <Button
            btnStyle={classNames({
              primary: !inCart,
              info: !!inCart,
            })}
            className={classNames({
              'purchasing-options-item-btn': true,
              'btn-purchase-options-in-cart': !!inCart,
            })}
            onClick={onToggle}
            data-testid="btn-digital-download"
          >
            {inCart ? <AddedToCartLabel /> : <Fragment>Add to cart</Fragment>}
          </Button>
        )}
      </div>
    </div>
  );
};

PhotoDownloadOption.propTypes = propTypes;

export default PhotoDownloadOption;
